import { useEffect, useMemo, useRef } from "react";
import { DateTime } from "luxon";
import { useParams, useSearchWithParams } from "@/hooks/useSearchParams";
import type { ApplicationStage, ApplicationType } from "../types";
import type { ApplicationStatus } from "../[applicationId]/mutations";

export type SubmittedOnPreset = "today" | "this_week" | "this_month";
export type AssignedFilter = "me" | "unassigned";

const STAGE_VALUES: ApplicationStage[] = [
  "draft",
  "submitted",
  "under_review",
  "changes_requested",
  "approved",
  "denied"
];

const TYPE_VALUES: ApplicationType[] = ["affiliate", "associate", "member"];
const SUBMITTED_ON_VALUES: SubmittedOnPreset[] = ["today", "this_week", "this_month"];
const ASSIGNED_VALUES: AssignedFilter[] = ["me", "unassigned"];

function isOneOf<T extends string>(value: string | undefined, allowed: readonly T[]): T | undefined {
  if (!value) return undefined;
  return (allowed as readonly string[]).includes(value) ? (value as T) : undefined;
}

export function submittedAfterFromPreset(preset: SubmittedOnPreset | undefined): string | undefined {
  if (!preset) return undefined;

  const now = DateTime.local();
  switch (preset) {
    case "today":
      return now.startOf("day").toISODate() ?? undefined;
    case "this_week":
      return now.startOf("week").toISODate() ?? undefined;
    case "this_month":
      return now.startOf("month").toISODate() ?? undefined;
    default: {
      const _exhaustive: never = preset;
      return _exhaustive;
    }
  }
}

export type UseApplicationFiltersOptions = {
  resetPagination: () => void;
  currentUserId?: string;
};

export default function useApplicationFilters({ resetPagination, currentUserId }: UseApplicationFiltersOptions) {
  const { params, addSearchParam, deleteSearchParams } = useParams();
  const { searchValue, debouncedSearch, setSearchValue, clearSearch } = useSearchWithParams({
    paramName: "search"
  });

  const stage = isOneOf(params.stage as string | undefined, STAGE_VALUES);
  const typeFilter = isOneOf(params.type as string | undefined, TYPE_VALUES);
  const sectionId = (params.section as string | undefined) || undefined;
  const submittedOn = isOneOf(params.submittedOn as string | undefined, SUBMITTED_ON_VALUES);
  const assigned = isOneOf(params.assigned as string | undefined, ASSIGNED_VALUES);

  function setStage(value: string) {
    if (!value || value === "all") {
      deleteSearchParams("stage");
      return;
    }
    addSearchParam("stage", value);
  }

  function setTypeFilter(value: string) {
    if (!value || value === "all") {
      deleteSearchParams("type");
      return;
    }
    addSearchParam("type", value);
  }

  function setSectionId(value: string) {
    if (!value || value === "all") {
      deleteSearchParams("section");
      return;
    }
    addSearchParam("section", value);
  }

  function setSubmittedOn(value: string) {
    if (!value || value === "all") {
      deleteSearchParams("submittedOn");
      return;
    }
    addSearchParam("submittedOn", value);
  }

  function setAssigned(value: AssignedFilter | undefined) {
    if (!value) {
      deleteSearchParams("assigned");
      return;
    }
    addSearchParam("assigned", value);
  }

  function toggleAssigned(next: AssignedFilter) {
    setAssigned(assigned === next ? undefined : next);
  }

  function clearAllFilters() {
    clearSearch();
    deleteSearchParams(["stage", "type", "section", "submittedOn", "assigned"]);
  }

  const filterState = {
    search: !!debouncedSearch,
    stage: !!stage,
    type: !!typeFilter,
    section: !!sectionId,
    submittedOn: !!submittedOn,
    assigned: !!assigned
  };

  const activeFilterCount = Object.values(filterState).filter(Boolean).length;

  // Avoid fetching an unfiltered list while the "assigned to me" chip is selected but
  // currentUserId has not loaded yet (capabilities/user bootstrap).
  const awaitingAssignedReviewer = assigned === "me" && !currentUserId;

  const queryVariables = useMemo(() => {
    const variables: {
      status?: ApplicationStatus;
      search?: string;
      sectionId?: string;
      reviewerId?: string;
      unassigned?: boolean;
      submittedAfter?: string;
      typeFilter?: ApplicationType;
    } = {};

    if (stage) variables.status = stage;
    if (debouncedSearch) variables.search = debouncedSearch;
    if (sectionId) variables.sectionId = sectionId;
    if (submittedOn) {
      const submittedAfter = submittedAfterFromPreset(submittedOn);
      if (submittedAfter) variables.submittedAfter = submittedAfter;
    }
    if (assigned === "unassigned") {
      variables.unassigned = true;
    } else if (assigned === "me" && currentUserId) {
      variables.reviewerId = currentUserId;
    }
    if (typeFilter) variables.typeFilter = typeFilter;

    return variables;
  }, [stage, debouncedSearch, sectionId, submittedOn, assigned, currentUserId, typeFilter]);

  const prevDepsRef = useRef({
    debouncedSearch,
    stage,
    typeFilter,
    sectionId,
    submittedOn,
    assigned
  });

  useEffect(() => {
    const prev = prevDepsRef.current;
    const changed =
      prev.debouncedSearch !== debouncedSearch ||
      prev.stage !== stage ||
      prev.typeFilter !== typeFilter ||
      prev.sectionId !== sectionId ||
      prev.submittedOn !== submittedOn ||
      prev.assigned !== assigned;

    if (changed) {
      resetPagination();
    }

    prevDepsRef.current = {
      debouncedSearch,
      stage,
      typeFilter,
      sectionId,
      submittedOn,
      assigned
    };
  }, [debouncedSearch, stage, typeFilter, sectionId, submittedOn, assigned, resetPagination]);

  return {
    searchValue,
    setSearchValue,
    clearSearch,
    stage: stage ?? "all",
    setStage,
    typeFilter: typeFilter ?? "all",
    setTypeFilter,
    sectionId: sectionId ?? "all",
    setSectionId,
    submittedOn: submittedOn ?? "all",
    setSubmittedOn,
    assigned,
    toggleAssigned,
    clearAllFilters,
    filterState,
    activeFilterCount,
    awaitingAssignedReviewer,
    queryVariables
  };
}
